import { useEffect, useRef } from 'react';

const FOCALISABLES = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Piège de focus pour un dialogue modal.
 *
 * Tant que `actif` est vrai :
 * - le focus entre dans le conteneur (premier élément focalisable, ou le
 *   conteneur lui-même, qui doit alors porter `tabIndex={-1}`) ;
 * - Tab / Maj+Tab bouclent à l'intérieur ;
 * - Échap appelle `onFermer`.
 * À la désactivation (ou au démontage), le focus revient à l'élément qui
 * l'avait avant l'ouverture — en général le bouton qui a ouvert le dialogue.
 *
 * Même comportement que ConfirmDialog, factorisé pour les autres dialogues
 * (lecteur, projets), qui laissaient le focus derrière la fenêtre.
 *
 * @param {boolean} actif
 * @param {() => void} [onFermer]
 * @returns {import('react').RefObject<HTMLElement>} à poser sur le conteneur.
 */
export default function usePiegeFocus(actif, onFermer) {
  const conteneurRef = useRef(null);
  // Le dernier `onFermer` sans relancer l'effet (et donc sans voler à nouveau
  // le focus) quand le parent recrée sa fonction à chaque rendu.
  const onFermerRef = useRef(onFermer);
  onFermerRef.current = onFermer;

  useEffect(() => {
    if (!actif) return undefined;
    const precedent = typeof document !== 'undefined' ? document.activeElement : null;
    const conteneur = conteneurRef.current;

    const focalisables = () =>
      conteneur ? Array.from(conteneur.querySelectorAll(FOCALISABLES)) : [];

    if (conteneur && !conteneur.contains(document.activeElement)) {
      const [premier] = focalisables();
      (premier || conteneur).focus?.();
    }

    const surTouche = (e) => {
      if (e.key === 'Escape') {
        if (onFermerRef.current) {
          e.preventDefault();
          e.stopPropagation();
          onFermerRef.current();
        }
        return;
      }
      if (e.key !== 'Tab' || !conteneur) return;
      const liste = focalisables();
      if (liste.length === 0) {
        e.preventDefault();
        return;
      }
      const premier = liste[0];
      const dernier = liste[liste.length - 1];
      const actifEl = document.activeElement;
      const dedans = conteneur.contains(actifEl);
      if (e.shiftKey && (actifEl === premier || !dedans)) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && (actifEl === dernier || !dedans)) {
        e.preventDefault();
        premier.focus();
      }
    };

    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('keydown', surTouche);
      if (precedent && typeof precedent.focus === 'function' && document.contains(precedent)) {
        precedent.focus();
      }
    };
  }, [actif]);

  return conteneurRef;
}
